/**
 * Churn Prediction Utility
 * Predicts the likelihood of a user churning based on behavioral patterns
 */

/**
 * Predict churn risk for a user
 * @param {Object} user - User object
 * @returns {Object} - { risk, score, factors, recommendations }
 */
export const predictChurnRisk = (user) => {
    let riskScore = 0;
    const factors = [];
    const recommendations = [];

    // Factor 1: Missed appointments (50% weight)
    const missedAppointments = getMissedAppointments(user);
    if (missedAppointments >= 3) {
        riskScore += 50;
        factors.push({
            factor: `${missedAppointments} missed appointments`,
            severity: 'high',
            impact: 50
        });
        recommendations.push('Schedule follow-up consultation');
    } else if (missedAppointments >= 1) {
        riskScore += 25;
        factors.push({
            factor: `${missedAppointments} missed appointment(s)`,
            severity: 'medium',
            impact: 25
        });
    }

    // Factor 2: Communication engagement (50% weight)
    const communicationScore = getCommunicationScore(user);
    if (communicationScore < 30) {
        riskScore += 50;
        factors.push({
            factor: 'Low communication engagement',
            severity: 'high',
            impact: 50
        });
        recommendations.push('Try alternative communication channels');
    } else if (communicationScore < 50) {
        riskScore += 25;
        factors.push({
            factor: 'Medium communication engagement',
            severity: 'medium',
            impact: 25
        });
        recommendations.push('Increase engagement frequency');
    }



    // Determine risk level
    const risk = getChurnRiskLevel(riskScore);

    return {
        risk,
        score: Math.min(riskScore, 100),
        factors,
        recommendations,
        priority: getRiskPriority(risk)
    };
};

/**
 * Get days since last top-up
 */
const getDaysSinceLastTopUp = (user) => {
    const lastTopUp = user.lastTopUpDate || user.shakeTopUp?.[0]?.date;
    if (!lastTopUp) return 999;

    const date = lastTopUp.seconds
        ? new Date(lastTopUp.seconds * 1000)
        : new Date(lastTopUp);

    return Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
};

/**
 * Analyze consumption trend
 */
const analyzeConsumptionTrend = (user) => {
    const topUpHistory = user.shakeTopUp || [];
    if (topUpHistory.length < 2) return 'insufficient_data';

    // Get last 3 top-ups
    const recentTopUps = topUpHistory.slice(0, 3);

    // Calculate average days between top-ups
    const intervals = [];
    for (let i = 0; i < recentTopUps.length - 1; i++) {
        const date1 = recentTopUps[i].date?.seconds
            ? new Date(recentTopUps[i].date.seconds * 1000)
            : new Date(recentTopUps[i].date || recentTopUps[i].createdAt);

        const date2 = recentTopUps[i + 1].date?.seconds
            ? new Date(recentTopUps[i + 1].date.seconds * 1000)
            : new Date(recentTopUps[i + 1].date || recentTopUps[i + 1].createdAt);

        const daysBetween = Math.floor((date1 - date2) / (1000 * 60 * 60 * 24));
        intervals.push(daysBetween);
    }

    // Check if intervals are increasing (declining consumption)
    if (intervals.length >= 2) {
        const isIncreasing = intervals[0] > intervals[1];
        if (isIncreasing) return 'declining';
    }

    // Check for irregularity
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const variance = intervals.some(interval => Math.abs(interval - avgInterval) > 14);
    if (variance) return 'irregular';

    return 'stable';
};

/**
 * Get missed appointments count
 */
const getMissedAppointments = (user) => {
    const appointments = user.appointments || [];
    return appointments.filter(apt => apt.status === 'missed' || apt.status === 'cancelled').length;
};

/**
 * Get communication score (0-100)
 */
const getCommunicationScore = (user) => {
    // This would ideally track message opens, responses, etc.
    // For now, return a default score
    const lastContactDate = user.lastContactDate;
    if (!lastContactDate) return 50;

    const date = lastContactDate.seconds
        ? new Date(lastContactDate.seconds * 1000)
        : new Date(lastContactDate);

    const daysSinceContact = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));

    if (daysSinceContact <= 7) return 100;
    if (daysSinceContact <= 14) return 75;
    if (daysSinceContact <= 30) return 50;
    return 25;
};

/**
 * Get churn risk level
 */
const getChurnRiskLevel = (score) => {
    if (score >= 70) return 'critical';
    if (score >= 50) return 'high';
    if (score >= 30) return 'medium';
    if (score >= 10) return 'low';
    return 'minimal';
};

/**
 * Get risk priority
 */
const getRiskPriority = (risk) => {
    const priorities = {
        critical: 1,
        high: 2,
        medium: 3,
        low: 4,
        minimal: 5
    };
    return priorities[risk] || 5;
};

/**
 * Get users by churn risk
 */
export const getUsersByChurnRisk = (users, riskLevel) => {
    return users
        .map(user => ({
            ...user,
            churnPrediction: predictChurnRisk(user)
        }))
        .filter(user => user.churnPrediction.risk === riskLevel)
        .sort((a, b) => b.churnPrediction.score - a.churnPrediction.score);
};

/**
 * Get all at-risk users (medium, high, critical)
 */
export const getAtRiskUsers = (users) => {
    return users
        .map(user => ({
            ...user,
            churnPrediction: predictChurnRisk(user)
        }))
        .filter(user => ['medium', 'high', 'critical'].includes(user.churnPrediction.risk))
        .sort((a, b) => a.churnPrediction.priority - b.churnPrediction.priority);
};

/**
 * Get churn risk color
 */
export const getChurnRiskColor = (risk) => {
    const colors = {
        critical: 'text-red-600',
        high: 'text-orange-600',
        medium: 'text-yellow-600',
        low: 'text-blue-600',
        minimal: 'text-green-600'
    };
    return colors[risk] || 'text-gray-600';
};

/**
 * Get churn risk background color
 */
export const getChurnRiskBgColor = (risk) => {
    const colors = {
        critical: 'bg-red-100',
        high: 'bg-orange-100',
        medium: 'bg-yellow-100',
        low: 'bg-blue-100',
        minimal: 'bg-green-100'
    };
    return colors[risk] || 'bg-gray-100';
};
